import React from 'react';
import { useIntl } from 'react-intl';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { releaseThemeMessages } from '../../themes/releaseThemeMessages';
import type { ProfileRelease } from '../profileReleases';
import { ProfileReleaseGrid } from './ProfileReleaseGrid';
import { profileToolMessages as t } from './profileToolMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Profile; each part uses its own slice of it */
import styles from '../Profile.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface ProfileToolReleaseProps {
  releases: readonly ProfileRelease[];
  value: string | undefined;
  /** The blurred cover takes the colours: the "Use the look of this release" switch shows. */
  hasLookSwitch: boolean;
  lookFromRelease: boolean;
  artistThemeId: ReleaseThemeId;
  onPick: (catalogItemUuid: string) => void;
  onLookChange: (fromRelease: boolean) => void;
}

/** "Which release" of the cover source (design b1, d2), with "Use the look of this release". */
const ProfileToolReleaseComponent: React.FC<ProfileToolReleaseProps> = ({
  releases,
  value,
  hasLookSwitch,
  lookFromRelease,
  artistThemeId,
  onPick,
  onLookChange,
}) => {
  const intl = useIntl();
  const picked = releases.find((release) => release.catalogItemUuid === value);
  return (
    <div className={styles.picker} data-testid="profile-tool-release">
      <div className="d-flex flex-wrap gap-2 align-items-baseline">
        <b>{intl.formatMessage(t.releaseTitle)}</b>
        <small className={styles.muted}>{intl.formatMessage(hasLookSwitch ? t.releaseHint : t.releaseHintPlain)}</small>
      </div>
      {releases.length === 0 ? (
        <span className={styles.meta}>{intl.formatMessage(t.releaseNone)}</span>
      ) : (
        <ProfileReleaseGrid
          releases={releases}
          value={value}
          label={intl.formatMessage(t.releaseTitle)}
          onPick={onPick}
        />
      )}
      {hasLookSwitch && picked !== undefined && (
        <div className="d-flex flex-wrap gap-3 align-items-center small">
          <div className="form-check form-switch m-0">
            <input
              id="profile-release-look"
              type="checkbox"
              className="form-check-input"
              checked={lookFromRelease}
              data-testid="profile-release-look"
              onChange={(event) => onLookChange(event.currentTarget.checked)}
            />
            <label className="form-check-label" htmlFor="profile-release-look">
              {intl.formatMessage(t.useReleaseLook)}
            </label>
          </div>
          <span className={styles.muted}>
            {intl.formatMessage(t.useReleaseLookNote, {
              artistTheme: intl.formatMessage(releaseThemeMessages[artistThemeId].name),
              title: picked.title,
            })}
          </span>
        </div>
      )}
    </div>
  );
};

export const ProfileToolRelease = TypedMemo(ProfileToolReleaseComponent);
